import axios from "axios";
import { GENERAL_BASE_URL } from "shared/constants";
import { generateAuthorizationHeaders } from "shared/utils/request";

export type QuotaUsageModel = {
  billingId: string;
  totalQuota: number;
  remainingQuota: number;
};

interface GroupAccessTypeMapping {
  groupId: string;
}

interface User {
  _id: string;
  groupAccessTypeMapping?: GroupAccessTypeMapping[];
}

interface Group {
  _id: string;
  billingId?: string;
}

interface DashboardResponse {
  users?: User[];
  groups?: Group[];
}

interface QuotaSummaryResponse {
  total_quota: number;
  remaining_quota: number;
}

export async function fetchQuotaUsage(
  token: string,
  userId: string
): Promise<QuotaUsageModel | null> {
  const headers = generateAuthorizationHeaders(token);

  try {
    // Pull the full dashboard so we can locate the user and their group
    const dashboardRes = await axios.get<DashboardResponse>(
      `${GENERAL_BASE_URL}/dashboard/`,
      { headers }
    );
    const dashboard = dashboardRes.data;

    const user = dashboard.users?.find((u) => u._id === userId);
    if (!user) return null;

    const mappings = user.groupAccessTypeMapping ?? [];
    if (!mappings.length) return null;

    // check the users groups until we find one that actually has billing set up
    let billingGroup: Group | undefined;
    for (const mapping of mappings) {
      const group = dashboard.groups?.find((g) => g._id === mapping.groupId);
      if (group?.billingId) {
        billingGroup = group;
        break;
      }
    }

    if (!billingGroup) return null;

    const billingId = billingGroup.billingId as string;

    // Fetch the quota breakdown for this billing entity
    const quotaSummaryRes = await axios.get<QuotaSummaryResponse>(
      `${GENERAL_BASE_URL}/groups/quota/${encodeURIComponent(billingId)}`,
      { headers }
    );
    const quotaSummary = quotaSummaryRes.data;

    return {
      billingId,
      totalQuota: quotaSummary.total_quota,
      remainingQuota: quotaSummary.remaining_quota,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Quota API request failed (${error.response?.status ?? "unknown"} – ${error.config?.url})`
      );
    }
    throw error;
  }
}